import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const GoalTrackingPanel = ({ filters }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedGoal, setSelectedGoal] = useState(null);

  const goals = [];

  const getStatusBadge = (status) => {
    const badges = {
      on_track: { label: 'On Track', color: 'bg-emerald-100 text-emerald-700', icon: 'CheckCircle' },
      at_risk: { label: 'At Risk', color: 'bg-amber-100 text-amber-700', icon: 'AlertCircle' },
      behind: { label: 'Behind', color: 'bg-red-100 text-red-700', icon: 'XCircle' },
      completed: { label: 'Completed', color: 'bg-blue-100 text-blue-700', icon: 'Check' }
    };
    return badges?.[status] || badges?.on_track;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Icon name="Target" size={28} className="text-blue-600" />
              Goal Setting & Tracking
            </h2>
            <p className="text-slate-600 mt-1">SMART objective creation with departmental alignment and progress monitoring</p>
          </div>
          <Button variant="primary" iconName="Plus" onClick={() => setShowCreateModal(true)}>
            Create New Goal
          </Button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
          <div className="p-4 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-900">Total Active Goals</span>
              <Icon name="Target" size={18} className="text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-blue-900">0</p>
          </div>

          <div className="p-4 bg-gradient-to-br from-emerald-50 to-emerald-100 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-emerald-900">On Track</span>
              <Icon name="CheckCircle" size={18} className="text-emerald-600" />
            </div>
            <p className="text-2xl font-bold text-emerald-900">0</p>
          </div>

          <div className="p-4 bg-gradient-to-br from-amber-50 to-amber-100 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-amber-900">At Risk</span>
              <Icon name="AlertCircle" size={18} className="text-amber-600" />
            </div>
            <p className="text-2xl font-bold text-amber-900">0</p>
          </div>

          <div className="p-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-purple-900">Completed</span>
              <Icon name="Check" size={18} className="text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-purple-900">0</p>
          </div>
        </div>
      </div>
      {/* Goals List */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        {goals?.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
              <Icon name="Target" size={40} className="text-slate-400" />
            </div>
            <h3 className="text-xl font-bold text-slate-900 mb-2">No Goals Yet</h3>
            <p className="text-slate-600 mb-6">Start by creating performance goals for your team members</p>
            <Button variant="primary" iconName="Plus" onClick={() => setShowCreateModal(true)}>
              Create First Goal
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Goal Title
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Target Date
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Next Review
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {goals?.map((goal) => {
                  const statusBadge = getStatusBadge(goal?.status);
                  return (
                    <tr key={goal?.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-slate-900">{goal?.employeeName}</p>
                          <p className="text-sm text-slate-500">{goal?.department}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-medium text-slate-900">{goal?.goalTitle}</p>
                          <p className="text-sm text-slate-500 line-clamp-1">{goal?.description}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="flex-1 bg-slate-200 rounded-full h-2 overflow-hidden">
                            <div
                              className="h-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all"
                              style={{ width: `${goal?.progress}%` }}
                            />
                          </div>
                          <span className="text-sm font-semibold text-slate-700 min-w-[45px]">{goal?.progress}%</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium ${statusBadge?.color}`}>
                          <Icon name={statusBadge?.icon} size={14} />
                          {statusBadge?.label}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-slate-700">{new Date(goal.targetDate)?.toLocaleDateString()}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <Icon name="Calendar" size={16} className="text-slate-400" />
                          <p className="text-sm text-slate-700">{new Date(goal.nextReview)?.toLocaleDateString()}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => setSelectedGoal(goal)}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Icon name="Eye" size={18} />
                          </button>
                          <button
                            className="p-2 text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                            title="Update Progress"
                          >
                            <Icon name="Edit" size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* Reminder System */}
      <div className="bg-gradient-to-br from-amber-50 to-amber-100 rounded-xl border border-amber-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-xl bg-amber-500 flex items-center justify-center flex-shrink-0">
            <Icon name="Bell" size={24} className="text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-lg font-bold text-amber-900 mb-2">Upcoming Review Reminders</h3>
            <p className="text-amber-800 mb-4">No upcoming reviews scheduled. Automated reminders will be sent to managers when reviews are due.</p>
            <Button variant="outline" iconName="Calendar" size="sm">
              View Review Schedule
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GoalTrackingPanel;